import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { Card, CardBody } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { Accordion, SelectInput, Button, Loading } from '@whitelabel/component-library';
import commonMessages from '@whitelabel/helpers/messages/commonMsg';
import api from '@whitelabel/xcover-shared/helpers/api';
import { getAPIHost } from '@whitelabel/xcover-shared/helpers/multiRegion';
import StyledSmallContentBlock from '@whitelabel/xcover-shared/styled/StyledSmallContentBlock';
import { ZendeskChatButton, useZendeskChat } from '@whitelabel/xcover-shared/components/PartnerZendeskChat';
import { IUser } from '@whitelabel/helpers/types';
import {
  ModalState,
  IHelpCenterArticlesValue,
  IContactArticleListsResponse,
  IHelpPagePartner,
} from '@whitelabel/xcover-shared/helpers/types';
import {
  StyledContactPurpose,
  StyledLabel,
  StyledButtonBlock,
  StyledDivider,
  StyledAlert,
} from '@whitelabel/xcover-shared/styled/StyledContactPurpose';
import contactModalMsg from '@whitelabel/xcover-shared/helpers/messages/contactModalMsg';
import PartnerHeader from '../ParterHeader';

interface IContactPurposeProps {
  toggleModalClose: () => void;
  partner: IHelpPagePartner;
  setStep: (step: ModalState) => void;
  enableReturn?: boolean;
  user?: IUser;
}

const ContactPurpose = ({
  toggleModalClose,
  partner,
  setStep,
  enableReturn,
  user,
}: IContactPurposeProps): JSX.Element => {
  const { locale, formatMessage, bidi } = useIntl();
  const history = useHistory();
  const [articlesTitle, setArticlesTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const { isZendeskChatEnabled } = useZendeskChat();
  const [error, setError] = useState('');
  const [helpPageContent, setHelpPageContent] = useState<IContactArticleListsResponse>();

  useEffect(() => {
    let cancelled = false;
    const getContent = async () => {
      try {
        setLoading(true);
        if (error) setError('');
        const result = await api.get(
          `${getAPIHost(undefined, 'v2')}/help/partners/${partner.slug}/?language=${locale}&isLoggedIn=${!!user}`,
          !!user,
        );
        if (result && !cancelled) {
          setHelpPageContent(result.helpPageContent);
        }
      } catch (e) {
        if (!cancelled) setError(formatMessage(commonMessages.error));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (!error && !helpPageContent) {
      getContent();
    }
    return () => {
      cancelled = true;
    };
  }, [partner, locale, helpPageContent, error, formatMessage]);

  const goToNextStep = () => {
    setStep('contact-form');
  };

  const goToHelpPage = () => {
    if (history.location.pathname !== `/${locale}/help/partners/${partner.slug}`) {
      history.push(`/${locale}/help/partners/${partner.slug}`);
    }
    toggleModalClose();
  };
  const returnPreviousStep = () => {
    setStep('multi-policy');
  };

  const renderArticles = (sectionValue: IHelpCenterArticlesValue) => {
    const { title: sectionTitle, items } = sectionValue;
    const accordionItems = items.map((item) => {
      const body = (
        <StyledSmallContentBlock $rtl={bidi} blocks={item.body} componentSpacing="sm" componentSize="small" />
      );
      return {
        title: item.title,
        anchorId: item.meta.pageSlug,
        body,
      };
    });

    return (
      <section key={sectionTitle} className="response-section">
        <StyledLabel>{formatMessage(contactModalMsg.articlesTitle)}</StyledLabel>
        <Accordion key={sectionTitle} items={accordionItems} />
      </section>
    );
  };

  const renderFooter = () => (
    <StyledButtonBlock>
      <Button buttonType="primary" onClick={goToHelpPage}>
        {formatMessage(contactModalMsg.allArticlesButtonText)}
      </Button>
      <Button buttonType="secondary" onClick={goToNextStep}>
        {formatMessage(
          isZendeskChatEnabled ? contactModalMsg.emailUsButtonText : contactModalMsg.moreQuestionsButtonText,
        )}
      </Button>
      <ZendeskChatButton partnerSlug={partner?.slug} />
    </StyledButtonBlock>
  );

  const renderArticlesSection = () => {
    const matchedArticle = helpPageContent?.sections.find(
      (section) => section.type === 'articles' && section.value.title === articlesTitle,
    );

    return (
      matchedArticle && (
        <>
          <StyledDivider />
          {renderArticles(matchedArticle.value)}
          <StyledDivider />
          {renderFooter()}
        </>
      )
    );
  };

  const renderArticleTitlesSection = () => {
    if (helpPageContent) {
      const articlesSections = helpPageContent.sections.filter((section) => section.type === 'articles');
      const options = articlesSections.map((section) => ({
        value: section.value.title,
        label: section.value.title,
      }));
      return (
        <section key="titles" className="response-section">
          <StyledLabel>{formatMessage(contactModalMsg.contactTitle)}</StyledLabel>
          <SelectInput
            id="contact-purpose"
            input={{
              name: 'contact-purpose',
              value: articlesTitle,
              onChange: (value: any) => {
                setArticlesTitle(value);
              },
            }}
            placeholder={formatMessage(contactModalMsg.placeholder)}
            options={options}
            isMulti={false}
          />
        </section>
      );
    }
    return undefined;
  };

  return (
    <StyledContactPurpose>
      {partner && (
        <PartnerHeader
          enableReturn={enableReturn}
          returnPreviousStep={returnPreviousStep}
          toggleModalClose={toggleModalClose}
          partner={partner}
        />
      )}
      {error && <StyledAlert color="danger">{formatMessage(commonMessages.error)}</StyledAlert>}
      <Card>
        <CardBody>
          {loading ? (
            <Loading />
          ) : (
            helpPageContent && (
              <>
                {renderArticleTitlesSection()}
                {renderArticlesSection()}
              </>
            )
          )}
        </CardBody>
      </Card>
    </StyledContactPurpose>
  );
};

const mapStateToProps = (state: any, props: any) => {
  const { partner } = props;
  const user = state.user.data;
  return {
    partner,
    user,
  };
};

export default connect(mapStateToProps)(ContactPurpose);
